import Box from '@mui/material/Box';
import { RouteComponentProps } from '@reach/router';
import React, { useState } from 'react';
import {
  publicationList as publicationListLocale,
  publicationView as publicationViewLocale
} from '../../../../locale/stories.en.locale.json';
import { IPublicationListMessages, IPublicationViewMessages, IStoriesFolder } from '../../../../types';
import { navigateToPublicationList, navigateToPublicationView } from '../../../state';
import { getNodeDescedentIds, transformFolderStructureToMap } from '../../../utils';
import { DeleteConfirmationModal } from '../../CommonComponents/DeleteConfirmationModal/DeleteConfirmationModal';
import { useDeletePublication } from '../PublicationList/hooks/usePublicationMutations';
import {
  useDeleteSectionById,
  useSetPublicationThumbnail,
  useUpdatePublicationUpdatedAt
} from '../PublicationView/hooks/usePublicationMutations';
import { styles } from './PublicationDeleteModal.styles';
import { Typography } from '@mui/material';

export interface IDeletePublicationModalProps extends RouteComponentProps {
  messages: IPublicationListMessages | IPublicationViewMessages;
  isDeleteSection: boolean;
  selectedPublicationId: string;
  publicationName: string;
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
  userId: number;
  isAdmin: boolean;
  selectedSectionId?: string;
  publicationUrl?: string;
  publicationFolderStructure?: IStoriesFolder[];
  isPublicationEmpty: boolean;
  isCustomSpreadsheetReportEnabled: boolean;
  onClose: () => void;
}

export const PublicationDeleteModal: React.FC<IDeletePublicationModalProps> = (props) => {
  const {
    publicationName,
    selectedPublicationId,
    messages,
    entityId,
    entityUuid,
    entitySubdomain,
    userId,
    isDeleteSection = false,
    selectedSectionId,
    publicationUrl,
    isCustomSpreadsheetReportEnabled,
    publicationFolderStructure = [],
    isPublicationEmpty = false
  } = props;
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteSectionMutation = useDeleteSectionById(
    { title: publicationViewLocale.sectionDeletedSuccessTitle, body: publicationViewLocale.sectionDeletedSuccessDesc },
    { title: publicationViewLocale.sectionDeletedFailedTitle, body: publicationViewLocale.sectionDeletedFailedDesc }
  );
  const updateTimestampMutation = useUpdatePublicationUpdatedAt();
  const setPublicationThumbnailMutation = useSetPublicationThumbnail();

  const deleteMutation = useDeletePublication(
    {
      title: publicationListLocale.publicationDeleted,
      // Inline interpolation — IntlMessageFormat would normally do this for
      // {title}, but we read the raw JSON here to avoid plumbing locale
      // selectors. English-only fallback; matches the behavior of the
      // retired effect for non-English locales (which would have hit the
      // selector path).
      body: publicationListLocale.publicationDeletedDescription.replace('{title}', `"${publicationName}"`)
    },
    {
      title: publicationListLocale.somethingWentWrongError,
      body: publicationListLocale.publicationFailedToDelete
    }
  );

  const onConfirmDeleteStory = () => {
    setIsDeleting(true);
    const publishedId = publicationUrl?.split('/').pop();
    deleteMutation.mutate(
      {
        publicationId: selectedPublicationId,
        publicationName,
        entityId,
        entityUuid,
        entitySubdomain,
        publishedId
      },
      {
        onSuccess: () => {
          // Navigate to list — no-op when already on list page; required
          // when triggered from PublicationView so the user isn't stranded
          // on a deleted publication.
          navigateToPublicationList(entitySubdomain);
          props.onClose();
        },
        onSettled: () => {
          setIsDeleting(false);
        }
      }
    );
  };

  const onConfirmDeleteSection = async () => {
    if (!selectedSectionId) return;
    setIsDeleting(true);
    const sectionMap = transformFolderStructureToMap(publicationFolderStructure);
    const currentSection = sectionMap?.[selectedSectionId];

    const descendentSectionIds = getNodeDescedentIds(currentSection);
    try {
      await deleteSectionMutation.mutateAsync({
        publicationId: selectedPublicationId,
        entityId: entityId,
        sectionIds: [selectedSectionId, ...descendentSectionIds]
      });
      updateTimestampMutation.mutate({ publicationId: selectedPublicationId, userId });
      // Refresh thumbnail + navigate away from the deleted section — preserves
      // the retired effect's post-delete behavior.
      void setPublicationThumbnailMutation.mutateAsync({
        publicationId: selectedPublicationId,
        entityId,
        entitySubdomain
      });
      navigateToPublicationView(entitySubdomain, selectedPublicationId);
    } catch {
      // Error toast emitted by useDeleteSectionById.
    } finally {
      setIsDeleting(false);
      props.onClose();
    }
  };

  const getDialogBody = (isPublicationEmpty: boolean) => {
    return isPublicationEmpty ? (
      <Box sx={styles.dialogBody}>
        <Typography variant="body1">
          {messages.deleteEmptyPublicationDescriptionPart1}
          <Typography variant="body1" sx={styles.boldText} component="span">
            &nbsp;{messages.deleteEmptyPublicationDescriptionPart2}&nbsp;
          </Typography>
          {messages.deleteEmptyPublicationDescriptionPart3}
        </Typography>
      </Box>
    ) : (
      <Box sx={styles.dialogBody}>
        <Typography variant="body1">
          {messages.deletedPublicationFolderText}
          <Typography variant="body1" sx={styles.boldText} component="span">
            &nbsp; {messages.deletedPublicationTitle({ title: `${publicationName}` })}
          </Typography>
        </Typography>

        {isCustomSpreadsheetReportEnabled ? (
          <Typography variant="body1">{messages.customReportWarning}</Typography>
        ) : null}
      </Box>
    );
  };

  const dialogDescription = isDeleteSection
    ? messages.deleteSectionDescription
    : !isPublicationEmpty
    ? messages.deletePublicationDescription
    : undefined;

  return (
    <DeleteConfirmationModal
      title={isDeleteSection ? messages.deleteSection : messages.deletePublication}
      message={
        <>
          {dialogDescription ? (
            <Box sx={styles.dialogDescription}>
              <Typography variant="body1">{dialogDescription}</Typography>
            </Box>
          ) : null}

          {isDeleteSection ? null : getDialogBody(isPublicationEmpty)}
        </>
      }
      isDeleting={isDeleting}
      onClose={props.onClose}
      onDelete={() => {
        if (isDeleteSection) {
          void onConfirmDeleteSection();
        } else {
          void onConfirmDeleteStory();
        }
      }}
      dataTest="dialog-delete-publication-Dialog"
    />
  );
};
